<template>
  <HrPageLayout
    title="Assets Management"
    subtitle="Track and manage company assets"
    icon="🖥️"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Assets' }]"
  >
    <template #header-actions>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button @click="exportAssets" class="hr-btn hr-btn-secondary">
        <i class="bx bx-export"></i> Export
      </button>
      <button @click="createAsset" class="hr-btn hr-btn-primary">
        <i class="bx bx-plus"></i> Add Asset
      </button>
    </template>

    <div class="assets-page">
      <!-- Metrics -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-desktop"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Assets</p>
          <h2 class="hr-metric-value">{{ metrics.totalAssets || 0 }}</h2>
            </div>
          <span class="hr-metric-hint">Overall inventory</span>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-user-check"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Assigned</p>
          <h2 class="hr-metric-value">{{ metrics.assignedAssets || 0 }}</h2>
            </div>
          <span class="hr-metric-hint">Currently in use</span>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-package"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Available</p>
          <h2 class="hr-metric-value">{{ metrics.availableAssets || 0 }}</h2>
            </div>
          <span class="hr-metric-hint">Ready to assign</span>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-wrench"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Maintenance</p>
          <h2 class="hr-metric-value">{{ metrics.maintenanceAssets || 0 }}</h2>
            </div>
          <span class="hr-metric-hint">Being serviced</span>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-filter-alt"></i> Filters</h5>
          <button v-if="hasActiveFilters" type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="resetFilters">
            Clear filters
          </button>
        </div>
        <div class="hr-chart-body">
          <div class="assets-filters-grid">
            <div class="assets-filter-field">
              <label class="hr-form-label">Asset Type</label>
              <select v-model="filters.assetType" class="hr-input">
                <option value="">All types</option>
                <option v-for="type in assetTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
            <div class="assets-filter-field">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.departmentId" class="hr-input">
                <option value="">All departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.departmentName }}</option>
              </select>
            </div>
            <div class="assets-filter-field">
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" class="hr-input">
                <option value="">All statuses</option>
                <option value="Active">Active</option>
                <option value="Assigned">Assigned</option>
                <option value="Under Maintenance">Under Maintenance</option>
                <option value="Disposed">Disposed</option>
              </select>
            </div>
            <div class="assets-filter-field">
              <label class="hr-form-label">Search</label>
              <div class="assets-search-wrap">
                <input
                  v-model="filters.search"
                  type="text"
                  class="hr-input"
                  placeholder="Name, tag, employee..."
                >
                <i class="bx bx-search assets-search-icon"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assets table -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title"><i class="bx bx-list-ul"></i> Assets Register</h5>
          <span class="assets-count-pill">{{ pagination.totalAssets || 0 }} total</span>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="assets-loading">
            <div class="hr-spinner"></div>
            <p>Loading assets...</p>
          </div>
          <div v-else-if="!assets.length" class="hr-empty-state">
            <i class="bx bx-box"></i>
            <h5>No assets found</h5>
            <p>Adjust filters or add a new asset to get started.</p>
            <button type="button" class="hr-btn hr-btn-primary" @click="createAsset">Add Asset</button>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover assets-table">
              <thead>
                <tr>
                  <th>Asset</th>
                  <th>Type</th>
                  <th>Department</th>
                  <th>Assigned To</th>
                  <th>Purchased</th>
                  <th>Status</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="asset in assets" :key="asset.id">
                  <td>
                    <div class="asset-cell">
                      <span class="asset-icon-wrap">
                        <i :class="getAssetIcon(asset.assetType)"></i>
                      </span>
                      <div>
                        <div class="asset-name">{{ asset.name }}</div>
                        <div class="asset-tag">{{ asset.tagNumber }}</div>
                      </div>
                    </div>
                  </td>
                  <td><span class="type-chip">{{ asset.assetType }}</span></td>
                  <td>{{ asset.departmentName || '—' }}</td>
                  <td>
                    <template v-if="asset.assignedTo">
                      <div class="asset-name">{{ asset.assignedToName }}</div>
                      <div class="asset-tag">{{ formatDate(asset.assignedDate) }}</div>
                    </template>
                    <span v-else class="hr-badge hr-badge-secondary">Unassigned</span>
                  </td>
                  <td>{{ formatDate(asset.purchaseDate) }}</td>
                  <td>
                    <span class="hr-badge" :class="getStatusBadgeClass(asset.status)">{{ asset.status }}</span>
                  </td>
                  <td>
                    <div class="assets-actions">
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" title="View" @click="viewAssetDetails(asset)">
                        <i class="bx bx-show"></i>
                      </button>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" title="Edit" @click="editAsset(asset)">
                        <i class="bx bx-edit"></i>
                      </button>
                      <button
                        v-if="asset.status !== 'Assigned'"
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-success"
                        title="Assign"
                        @click="assignAsset(asset)"
                      >
                        <i class="bx bx-user-plus"></i>
                      </button>
                      <button
                        v-else
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-warning"
                        title="Unassign"
                        @click="unassignAsset(asset)"
                      >
                        <i class="bx bx-user-minus"></i>
                      </button>
                      <button
                        v-if="asset.status !== 'Disposed'"
                        type="button"
                        class="hr-btn hr-btn-sm hr-btn-danger"
                        title="Dispose"
                        @click="disposeAsset(asset)"
                      >
                        <i class="bx bx-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="assets.length" class="assets-pagination">
            <span class="assets-page-info">
              Showing <strong>{{ assets.length }}</strong> of <strong>{{ pagination.totalAssets }}</strong>
            </span>
            <nav v-if="pagination.totalPages > 1" class="assets-page-nav">
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage <= 1" @click="changePage(pagination.currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <button
                v-for="page in getPageNumbers()"
                :key="'p-' + page"
                type="button"
                class="hr-page-btn"
                :class="{ active: page === pagination.currentPage, ellipsis: page === '...' }"
                :disabled="page === '...'"
                @click="page !== '...' && changePage(page)"
              >
                {{ page }}
              </button>
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage >= pagination.totalPages" @click="changePage(pagination.currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>

      <!-- Insights row -->
      <div class="assets-insights-grid">
        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-pie-chart-alt-2"></i> Assets by Type</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length" class="type-breakdown-list">
              <div v-for="(count, type) in metrics.typeCounts" :key="type" class="type-breakdown-item">
                <div class="type-breakdown-head">
                  <span>{{ type }}</span>
                  <strong>{{ count }} · {{ getAssetTypePercentage(type) }}%</strong>
                </div>
                <div class="type-breakdown-bar">
                  <span :style="{ width: getAssetTypePercentage(type) + '%' }" :class="getAssetTypeBarClass(type)"></span>
                </div>
              </div>
            </div>
            <div v-else class="hr-empty-state compact">
              <i class="bx bx-bar-chart-alt-2"></i>
              <p>No breakdown data yet</p>
            </div>
          </div>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header">
            <h5 class="hr-chart-title"><i class="bx bx-time-five"></i> Recent Activity</h5>
          </div>
          <div class="hr-chart-body">
            <div v-if="recentActivities.length" class="activity-feed">
              <div v-for="activity in recentActivities" :key="activity.id" class="activity-feed-item">
                <span class="activity-dot" :class="getActivityIconClass(activity.type)">
                  <i :class="getActivityIcon(activity.type)"></i>
                </span>
                <div class="activity-feed-body">
                  <div class="activity-feed-title">{{ activity.title }}</div>
                  <div class="activity-feed-desc">{{ activity.description }}</div>
                  <div class="activity-feed-meta">
                    {{ formatDate(activity.date) }} · {{ activity.user }}
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="hr-empty-state compact">
              <i class="bx bx-history"></i>
              <p>No recent activity</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  <!-- Asset Create/Edit Modal -->
  <div v-if="showAssetModal" class="hr-modal-overlay" @click.self="showAssetModal = false">
    <div class="hr-modal-dialog-lg">
      <div class="hr-modal-content">
        <div class="hr-modal-header hr-flex-between">
          <h5 class="hr-modal-title">{{ isEditMode ? 'Edit Asset' : 'Add New Asset' }}</h5>
          <button type="button" class="hr-modal-close" @click="showAssetModal = false">×</button>
        </div>
        <div class="hr-modal-body">
        <form @submit.prevent="submitAsset">
          <div class="asset-form-grid">
            <div class="form-group">
              <label class="form-label">Asset Name <span class="required">*</span></label>
              <input v-model="assetForm.name" type="text" class="form-input" required>
            </div>

            <div class="form-group">
              <label class="form-label">Asset Type <span class="required">*</span></label>
              <select v-model="assetForm.assetType" class="form-select" required>
                <option value="" disabled>Select Asset Type</option>
                <option v-for="type in assetTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">Tag Number <span class="required">*</span></label>
              <input v-model="assetForm.tagNumber" type="text" class="form-input" required>
            </div>

            <div class="form-group">
              <label class="form-label">Serial Number</label>
              <input v-model="assetForm.serialNumber" type="text" class="form-input">
            </div>

            <div class="form-group">
              <label class="form-label">Purchase Date <span class="required">*</span></label>
              <input v-model="assetForm.purchaseDate" type="date" class="form-input" required>
            </div>

            <div class="form-group">
              <label class="form-label">Purchase Cost <span class="required">*</span></label>
              <input v-model="assetForm.purchaseCost" type="number" step="0.01" class="form-input" required>
            </div>

            <div class="form-group">
              <label class="form-label">Department</label>
              <select v-model="assetForm.departmentId" class="form-select">
                <option value="">None</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName }}
                </option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">Status <span class="required">*</span></label>
              <select v-model="assetForm.status" class="form-select" required>
                <option value="Active">Active</option>
                <option value="Under Maintenance">Under Maintenance</option>
                <option value="Disposed">Disposed</option>
                <option value="Assigned">Assigned</option>
              </select>
            </div>

            <div class="form-group">
              <label class="form-label">Warranty Expiry Date</label>
              <input v-model="assetForm.warrantyExpiry" type="date" class="form-input">
            </div>

            <div class="form-group">
              <label class="form-label">Supplier</label>
              <input v-model="assetForm.supplier" type="text" class="form-input">
            </div>

            <div class="form-group full-width">
              <label class="form-label">Notes</label>
              <textarea v-model="assetForm.notes" class="form-textarea" rows="3"></textarea>
            </div>

            <div class="form-group full-width">
              <label class="form-label">Documents</label>
              <input type="file" class="form-input" multiple>
            </div>
          </div>
        </form>
        </div>
        <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showAssetModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="submitAsset">
            {{ isEditMode ? 'Update Asset' : 'Save Asset' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Asset Details Modal -->
  <div v-if="showDetailsModal && selectedAsset" class="hr-modal-overlay" @click.self="closeDetailsModal">
    <div class="hr-modal-dialog-xl">
      <div class="hr-modal-content">
        <div class="hr-modal-header hr-flex-between hr-modal-header-primary">
          <h5 class="hr-modal-title text-white">Asset Details</h5>
          <button type="button" class="hr-modal-close text-white" @click="closeDetailsModal">×</button>
        </div>
        <div class="hr-modal-body">
          <div class="assets-details-hero">
            <div>
              <h4 class="assets-details-title">{{ selectedAsset.name }}</h4>
              <div class="assets-details-meta">
                <span class="type-chip">{{ selectedAsset.assetType }}</span>
                <span class="hr-badge" :class="getStatusBadgeClass(selectedAsset.status)">{{ selectedAsset.status }}</span>
                <span class="asset-tag">Tag: {{ selectedAsset.tagNumber }}</span>
              </div>
            </div>
            <div class="assets-details-actions">
              <button
                v-if="selectedAsset.status !== 'Assigned'"
                type="button"
                class="hr-btn hr-btn-sm hr-btn-success"
                @click="assignAsset(selectedAsset)"
              >
                <i class="bx bx-user-plus"></i> Assign
              </button>
              <button
                v-else
                type="button"
                class="hr-btn hr-btn-sm hr-btn-warning"
                @click="unassignAsset(selectedAsset)"
              >
                <i class="bx bx-user-minus"></i> Unassign
              </button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="addMaintenanceRecord(selectedAsset)">
                <i class="bx bx-wrench"></i> Maintenance
              </button>
              <button
                v-if="selectedAsset.status !== 'Disposed'"
                type="button"
                class="hr-btn hr-btn-sm hr-btn-danger"
                @click="disposeAsset(selectedAsset)"
              >
                <i class="bx bx-trash"></i> Dispose
              </button>
            </div>
          </div>

          <div class="assets-details-grid">
            <div class="assets-detail-panel">
              <h6 class="assets-detail-panel-title"><i class="bx bx-info-circle"></i> Asset Information</h6>
              <dl class="assets-detail-list">
                <div><dt>Serial Number</dt><dd>{{ selectedAsset.serialNumber || 'N/A' }}</dd></div>
                <div><dt>Purchase Date</dt><dd>{{ formatDate(selectedAsset.purchaseDate) }}</dd></div>
                <div><dt>Purchase Cost</dt><dd>{{ formatCurrency(selectedAsset.purchaseCost, selectedAsset.currency) }}</dd></div>
                <div><dt>Warranty Until</dt><dd>{{ formatDate(selectedAsset.warrantyExpiry) || 'N/A' }}</dd></div>
                <div><dt>Department</dt><dd>{{ selectedAsset.departmentName || 'N/A' }}</dd></div>
                <div><dt>Supplier</dt><dd>{{ selectedAsset.supplier || 'N/A' }}</dd></div>
              </dl>
            </div>

            <div class="assets-detail-panel">
              <h6 class="assets-detail-panel-title"><i class="bx bx-user"></i> Assignment</h6>
              <div v-if="selectedAsset.assignedTo" class="assets-assignee">
                <div class="assets-assignee-avatar">{{ getInitials(selectedAsset.assignedToName) }}</div>
                <div>
                  <div class="asset-name">{{ selectedAsset.assignedToName }}</div>
                  <div class="asset-tag">{{ selectedAsset.assignedToDepartment }}</div>
                </div>
              </div>
              <div v-else class="assets-unassigned">
                <i class="bx bx-user-x"></i>
                <p>Not assigned to any employee</p>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="assignAsset(selectedAsset)">
                  <i class="bx bx-user-plus"></i> Assign Now
                </button>
              </div>
              <dl v-if="selectedAsset.assignedTo" class="assets-detail-list hr-mt-3">
                <div><dt>Assigned Date</dt><dd>{{ formatDate(selectedAsset.assignedDate) }}</dd></div>
                <div><dt>Assigned By</dt><dd>{{ selectedAsset.assignedBy || 'N/A' }}</dd></div>
                <div class="full-width"><dt>Notes</dt><dd>{{ selectedAsset.assignmentNotes || 'No notes provided' }}</dd></div>
              </dl>
            </div>
          </div>

          <div v-if="selectedAsset.maintenanceHistory && selectedAsset.maintenanceHistory.length" class="assets-detail-panel hr-mt-4">
            <h6 class="assets-detail-panel-title"><i class="bx bx-wrench"></i> Maintenance History</h6>
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-sm">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Cost</th>
                    <th>Performed By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(maintenance, index) in selectedAsset.maintenanceHistory" :key="index">
                    <td>{{ formatDate(maintenance.date) }}</td>
                    <td>{{ maintenance.type }}</td>
                    <td>{{ maintenance.description }}</td>
                    <td>{{ formatCurrency(maintenance.cost, selectedAsset.currency) }}</td>
                    <td>{{ maintenance.performedBy }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="assets-detail-panel hr-mt-4">
            <h6 class="assets-detail-panel-title"><i class="bx bx-history"></i> Asset History</h6>
            <div v-if="selectedAsset.history && selectedAsset.history.length" class="timeline-container">
              <div v-for="(event, index) in selectedAsset.history" :key="index" class="timeline-item">
                <div class="timeline-marker" :class="getTimelineMarkerClass(event.type)">
                  <i :class="getTimelineIcon(event.type)"></i>
                </div>
                <div class="timeline-content">
                  <h6 class="timeline-title">{{ event.title }}</h6>
                  <p class="timeline-desc">{{ event.description }}</p>
                  <small class="timeline-meta">
                    {{ formatDateTime(event.timestamp) }} · {{ event.user }}
                  </small>
                </div>
              </div>
            </div>
            <div v-else class="hr-empty-state compact">
              <i class="bx bx-history"></i>
              <p>No history available for this asset</p>
            </div>
          </div>
        </div>
        <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
          <button type="button" class="hr-btn hr-btn-secondary" @click="closeDetailsModal">Close</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="editAsset(selectedAsset)">
            <i class="bx bx-edit"></i> Edit
          </button>
          <button type="button" class="hr-btn hr-btn-secondary" @click="exportAssetDetails(selectedAsset)">
            <i class="bx bx-download"></i> Export
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Assign Asset Modal -->
  <div v-if="showAssignModal && selectedAsset" class="hr-modal-overlay" @click.self="showAssignModal = false">
    <div class="hr-modal-dialog-sm">
      <div class="hr-modal-content">
        <div class="hr-modal-header hr-flex-between">
          <h5 class="hr-modal-title">Assign Asset</h5>
          <button type="button" class="hr-modal-close" @click="showAssignModal = false">×</button>
        </div>
        <div class="hr-modal-body">
          <p class="hr-mb-3">
            Assigning <strong>{{ selectedAsset.name }}</strong>
            <span class="type-chip">{{ selectedAsset.assetType }}</span>
          </p>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Employee <span class="text-danger">*</span></label>
            <select v-model="assignmentForm.employeeId" class="hr-input" required>
              <option value="" disabled>Select employee</option>
              <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                {{ employee.firstName }} {{ employee.lastName }} — {{ employee.departmentName }}
              </option>
            </select>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Assignment date <span class="text-danger">*</span></label>
            <input v-model="assignmentForm.assignmentDate" type="date" class="hr-input" required>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Notes</label>
            <textarea v-model="assignmentForm.notes" class="hr-input" rows="3" placeholder="Optional notes..."></textarea>
          </div>
          <label class="assets-check">
            <input v-model="assignmentForm.notifyEmployee" type="checkbox">
            <span>Notify employee by email</span>
          </label>
        </div>
        <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showAssignModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-success" @click="confirmAssignAsset">Assign Asset</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Unassign Asset Modal -->
  <div v-if="showUnassignModal && selectedAsset" class="hr-modal-overlay" @click.self="showUnassignModal = false">
    <div class="hr-modal-dialog-sm">
      <div class="hr-modal-content">
        <div class="hr-modal-header hr-flex-between">
          <h5 class="hr-modal-title">Unassign Asset</h5>
          <button type="button" class="hr-modal-close" @click="showUnassignModal = false">×</button>
        </div>
        <div class="hr-modal-body">
          <p class="hr-mb-3">
            Unassign <strong>{{ selectedAsset.name }}</strong> from <strong>{{ selectedAsset.assignedToName }}</strong>?
          </p>
          <div class="assets-alert assets-alert-warning hr-mb-3">
            This removes the asset from the employee and makes it available again.
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Return date <span class="text-danger">*</span></label>
            <input v-model="unassignmentForm.returnDate" type="date" class="hr-input" required>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Return condition</label>
            <select v-model="unassignmentForm.returnCondition" class="hr-input">
              <option value="Excellent">Excellent</option>
              <option value="Good">Good</option>
              <option value="Fair">Fair</option>
              <option value="Poor">Poor</option>
              <option value="Damaged">Damaged</option>
            </select>
          </div>
          <div class="assets-filter-field">
            <label class="hr-form-label">Notes</label>
            <textarea v-model="unassignmentForm.notes" class="hr-input" rows="3"></textarea>
          </div>
        </div>
        <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showUnassignModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-warning" @click="confirmUnassignAsset">Unassign</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Dispose Asset Modal -->
  <div v-if="showDisposeModal && selectedAsset" class="hr-modal-overlay" @click.self="showDisposeModal = false">
    <div class="hr-modal-dialog-sm">
      <div class="hr-modal-content">
        <div class="hr-modal-header hr-flex-between">
          <h5 class="hr-modal-title">Dispose Asset</h5>
          <button type="button" class="hr-modal-close" @click="showDisposeModal = false">×</button>
        </div>
        <div class="hr-modal-body">
          <p class="hr-mb-3">Mark <strong>{{ selectedAsset.name }}</strong> as disposed?</p>
          <div class="assets-alert assets-alert-danger hr-mb-3">This cannot be undone.</div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Disposal date <span class="text-danger">*</span></label>
            <input v-model="disposalForm.disposalDate" type="date" class="hr-input" required>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Method <span class="text-danger">*</span></label>
            <select v-model="disposalForm.disposalMethod" class="hr-input" required>
              <option value="" disabled>Select method</option>
              <option value="Sold">Sold</option>
              <option value="Recycled">Recycled</option>
              <option value="Donated">Donated</option>
              <option value="Discarded">Discarded</option>
              <option value="Returned to Supplier">Returned to Supplier</option>
            </select>
          </div>
          <div v-if="disposalForm.disposalMethod === 'Sold'" class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Sale amount</label>
            <input v-model="disposalForm.saleAmount" type="number" step="0.01" class="hr-input">
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Reason <span class="text-danger">*</span></label>
            <textarea v-model="disposalForm.reason" class="hr-input" rows="3" required></textarea>
          </div>
          <div class="assets-filter-field">
            <label class="hr-form-label">Authorized by</label>
            <input v-model="disposalForm.authorizedBy" type="text" class="hr-input">
          </div>
        </div>
        <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showDisposeModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-danger" @click="confirmDisposeAsset">Dispose Asset</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Maintenance Modal -->
  <div v-if="showMaintenanceModal && selectedAsset" class="hr-modal-overlay" @click.self="showMaintenanceModal = false">
    <div class="hr-modal-dialog-sm">
      <div class="hr-modal-content">
        <div class="hr-modal-header hr-flex-between">
          <h5 class="hr-modal-title">Add Maintenance Record</h5>
          <button type="button" class="hr-modal-close" @click="showMaintenanceModal = false">×</button>
        </div>
        <div class="hr-modal-body">
          <p class="hr-mb-3">Maintenance for <strong>{{ selectedAsset.name }}</strong></p>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Date <span class="text-danger">*</span></label>
            <input v-model="maintenanceForm.date" type="date" class="hr-input" required>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Type <span class="text-danger">*</span></label>
            <select v-model="maintenanceForm.type" class="hr-input" required>
              <option value="" disabled>Select type</option>
              <option value="Repair">Repair</option>
              <option value="Scheduled Maintenance">Scheduled Maintenance</option>
              <option value="Upgrade">Upgrade</option>
              <option value="Cleaning">Cleaning</option>
              <option value="Inspection">Inspection</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Description <span class="text-danger">*</span></label>
            <textarea v-model="maintenanceForm.description" class="hr-input" rows="3" required></textarea>
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Cost</label>
            <input v-model="maintenanceForm.cost" type="number" step="0.01" class="hr-input">
          </div>
          <div class="assets-filter-field hr-mb-3">
            <label class="hr-form-label">Performed by</label>
            <input v-model="maintenanceForm.performedBy" type="text" class="hr-input">
          </div>
          <div class="assets-filter-field">
            <label class="hr-form-label">Status after maintenance</label>
            <select v-model="maintenanceForm.newStatus" class="hr-input">
              <option value="Active">Active</option>
              <option value="Under Maintenance">Still Under Maintenance</option>
            </select>
          </div>
        </div>
        <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showMaintenanceModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" @click="confirmAddMaintenance">Save Record</button>
        </div>
      </div>
    </div>
  </div>
  </HrPageLayout>
</template>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.assets-page { display: flex; flex-direction: column; gap: 0; }

.assets-filters-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}
@media (max-width: 992px) {
  .assets-filters-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 576px) {
  .assets-filters-grid { grid-template-columns: 1fr; }
}

.assets-filter-field { display: flex; flex-direction: column; gap: 6px; }
.assets-search-wrap { position: relative; }
.assets-search-wrap .hr-input { padding-right: 36px; }
.assets-search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.assets-count-pill {
  font-size: 0.8rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 999px;
}

.assets-loading {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
}

.asset-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}
.asset-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #5b73e8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.asset-name { font-weight: 600; color: #1e293b; }
.asset-tag { font-size: 0.78rem; color: #94a3b8; }

.type-chip {
  display: inline-block;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
}

.assets-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
}

.assets-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}
.assets-page-info { font-size: 0.875rem; color: #64748b; }
.assets-page-nav { display: flex; gap: 4px; }

.assets-insights-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 992px) {
  .assets-insights-grid { grid-template-columns: 1fr; }
}

.type-breakdown-list { display: flex; flex-direction: column; gap: 14px; }
.type-breakdown-head {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  margin-bottom: 6px;
  color: #334155;
}
.type-breakdown-bar {
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}
.type-breakdown-bar span {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}
.bar-primary { background: #3b82f6; }
.bar-info { background: #06b6d4; }
.bar-success { background: #10b981; }
.bar-warning { background: #f59e0b; }
.bar-danger { background: #ef4444; }
.bar-purple { background: #8b5cf6; }
.bar-muted { background: #94a3b8; }

.activity-feed { display: flex; flex-direction: column; gap: 12px; }
.activity-feed-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}
.activity-dot {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
}
.dot-primary { background: #dbeafe; color: #1d4ed8; }
.dot-info { background: #e0f2fe; color: #0369a1; }
.dot-success { background: #d1fae5; color: #047857; }
.dot-warning { background: #fef3c7; color: #b45309; }
.dot-danger { background: #fee2e2; color: #b91c1c; }
.dot-purple { background: #ede9fe; color: #6d28d9; }
.dot-muted { background: #f1f5f9; color: #64748b; }

.activity-feed-title { font-weight: 600; font-size: 0.875rem; color: #1e293b; }
.activity-feed-desc { font-size: 0.8rem; color: #64748b; margin-top: 2px; }
.activity-feed-meta { font-size: 0.75rem; color: #94a3b8; margin-top: 4px; }

.assets-alert {
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.875rem;
}
.assets-alert-warning { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
.assets-alert-danger { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }

.assets-check {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #475569;
  cursor: pointer;
}

.hr-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 16px;
}
.hr-modal-dialog-sm { width: min(480px, 100%); max-height: 90vh; overflow-y: auto; }
.hr-modal-dialog-lg { width: min(720px, 100%); max-height: 90vh; overflow-y: auto; }
.hr-modal-dialog-xl { width: min(960px, 100%); max-height: 90vh; overflow-y: auto; }
.hr-modal-content {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  overflow: hidden;
}
.hr-modal-header {
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
}
.hr-modal-header-primary {
  background: linear-gradient(135deg, #5b73e8, #7c3aed);
  border-bottom: none;
}
.hr-modal-title { margin: 0; font-size: 1.1rem; }
.hr-modal-close {
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}
.hr-modal-body { padding: 20px; }
.hr-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
}

.asset-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 576px) {
  .asset-form-grid { grid-template-columns: 1fr; }
}
.form-group { display: flex; flex-direction: column; }
.form-group.full-width { grid-column: 1 / -1; }
.form-label { margin-bottom: 6px; font-size: 0.875rem; font-weight: 500; color: #334155; }
.required { color: #dc2626; }
.form-input,
.form-select,
.form-textarea {
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1e293b;
  background: #fff;
}
.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #5b73e8;
  box-shadow: 0 0 0 3px rgba(91, 115, 232, 0.15);
}
.form-textarea { resize: vertical; min-height: 90px; }

.assets-details-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.assets-details-title {
  margin: 0 0 8px;
  font-size: 1.25rem;
  color: #1e293b;
}
.assets-details-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.assets-details-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.assets-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
@media (max-width: 768px) {
  .assets-details-grid { grid-template-columns: 1fr; }
}

.assets-detail-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
}
.assets-detail-panel-title {
  margin: 0 0 12px;
  font-size: 0.9rem;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 6px;
}
.assets-detail-list {
  margin: 0;
  display: grid;
  gap: 10px;
}
.assets-detail-list > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.875rem;
}
.assets-detail-list > div.full-width { flex-direction: column; }
.assets-detail-list dt { color: #64748b; font-weight: 500; }
.assets-detail-list dd { margin: 0; color: #1e293b; text-align: right; }
.assets-detail-list > div.full-width dd { text-align: left; }

.assets-assignee {
  display: flex;
  align-items: center;
  gap: 12px;
}
.assets-assignee-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5b73e8, #7c3aed);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
}
.assets-unassigned {
  text-align: center;
  padding: 1rem 0;
  color: #94a3b8;
}
.assets-unassigned i { font-size: 2rem; display: block; margin-bottom: 8px; }
.assets-unassigned p { margin: 0 0 12px; font-size: 0.875rem; }

.timeline-container {
  position: relative;
  padding-left: 2rem;
}
.timeline-item {
  position: relative;
  padding-bottom: 1.25rem;
}
.timeline-item:last-child { padding-bottom: 0; }
.timeline-marker {
  position: absolute;
  left: -2rem;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
}
.marker-primary { background: #3b82f6; }
.marker-info { background: #06b6d4; }
.marker-success { background: #10b981; }
.marker-warning { background: #f59e0b; }
.marker-danger { background: #ef4444; }
.marker-muted { background: #94a3b8; }

.timeline-content {
  background: #fff;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}
.timeline-title { margin: 0 0 4px; font-size: 0.875rem; color: #1e293b; }
.timeline-desc { margin: 0 0 6px; font-size: 0.8rem; color: #64748b; }
.timeline-meta { color: #94a3b8; font-size: 0.75rem; }
</style>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "./HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "Assets",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      assets: [],
      departments: [],
      employees: [],
      assetTypes: [
        'Computer',
        'Laptop',
        'Mobile Phone',
        'Tablet',
        'Monitor',
        'Printer',
        'Furniture',
        'Vehicle',
        'Boats',
        'Office Equipment',
        'Software',
        'Other'
      ],
      filters: {
        assetType: "",
        departmentId: "",
        status: "",
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalAssets: 0,
        limit: 10
      },
      metrics: {
        totalAssets: 0,
        assignedAssets: 0,
        availableAssets: 0,
        maintenanceAssets: 0,
        typeCounts: {}
      },
      selectedAsset: null,
      assetForm: {
        name: "",
        assetType: "",
        tagNumber: "",
        serialNumber: "",
        purchaseDate: "",
        purchaseCost: "",
        departmentId: "",
        status: "Active",
        warrantyExpiry: "",
        supplier: "",
        notes: ""
      },
      assignmentForm: {
        employeeId: "",
        assignmentDate: new Date().toISOString().split('T')[0],
        notes: "",
        notifyEmployee: true
      },
      unassignmentForm: {
        returnDate: new Date().toISOString().split('T')[0],
        returnCondition: "Good",
        notes: ""
      },
      disposalForm: {
        disposalDate: new Date().toISOString().split('T')[0],
        disposalMethod: "",
        saleAmount: 0,
        reason: "",
        authorizedBy: ""
      },
      maintenanceForm: {
        date: new Date().toISOString().split('T')[0],
        type: "",
        description: "",
        cost: 0,
        performedBy: "",
        newStatus: "Active"
      },
      isEditMode: false,
      recentActivities: [],
      showAssetModal: false,
      showDetailsModal: false,
      showAssignModal: false,
      showUnassignModal: false,
      showDisposeModal: false,
      showMaintenanceModal: false
    };
  },
  computed: {
    hasActiveFilters() {
      return !!(this.filters.assetType || this.filters.departmentId || this.filters.status || this.filters.search);
    }
  },
  mounted() {
    this.cleanupBootstrapModalArtifacts();
    this.getDepartments();
    this.getEmployees();
    this.getAssets();
    this.getRecentActivities();
  },
  beforeUnmount() {
    this.cleanupBootstrapModalArtifacts();
  },
  methods: {
    cleanupBootstrapModalArtifacts() {
      document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());
      document.body.classList.remove('modal-open');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('padding-right');
    },
    closeDetailsModal() {
      this.showDetailsModal = false;
    },
    // Core data fetching methods
    getAssets() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAssets");

      // Add filter parameters
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key]) {
          data.append(key, this.filters[key]);
        }
      });

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.assets = res.data.data.assets || [];
              this.metrics = res.data.data.metrics || {};
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalAssets: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching assets:", res.data.message);
              this.showToast("error", "Failed to load assets data: " + res.data.message);
              this.assets = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching assets");
            this.assets = [];
            this.metrics = {};
          });
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    getRecentActivities() {
      const data = new FormData();
      data.append("function", "getAssetActivities");
      data.append("limit", 5);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.recentActivities = res.data.data.activities || [];
            } else {
              console.error("Error fetching asset activities:", res.data.message);
              this.recentActivities = [];
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.recentActivities = [];
          });
    },

    // UI interaction methods
    refreshData() {
      this.getAssets();
      this.getRecentActivities();
    },

    resetFilters() {
      this.filters = {
        assetType: "",
        departmentId: "",
        status: "",
        search: "",
        page: 1,
        limit: 10
      };
      this.getAssets();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getAssets();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    // Asset CRUD operations
    createAsset() {
      this.isEditMode = false;
      this.assetForm = {
        name: "",
        assetType: "",
        tagNumber: "",
        serialNumber: "",
        purchaseDate: new Date().toISOString().split('T')[0],
        purchaseCost: "",
        departmentId: "",
        status: "Active",
        warrantyExpiry: "",
        supplier: "",
        notes: ""
      };
      this.showAssetModal = true;
    },

    editAsset(asset) {
      this.isEditMode = true;
      this.selectedAsset = asset;
      this.showDetailsModal = false;

      // Prepare form data
      this.assetForm = {
        id: asset.id,
        name: asset.name,
        assetType: asset.assetType,
        tagNumber: asset.tagNumber,
        serialNumber: asset.serialNumber || "",
        purchaseDate: asset.purchaseDate,
        purchaseCost: asset.purchaseCost,
        departmentId: asset.departmentId || "",
        status: asset.status,
        warrantyExpiry: asset.warrantyExpiry || "",
        supplier: asset.supplier || "",
        notes: asset.notes || ""
      };

      // Open the asset modal
      this.showAssetModal = true;
    },

    submitAsset() {
      // Validate required fields
      if (!this.assetForm.name || !this.assetForm.assetType || !this.assetForm.tagNumber || !this.assetForm.purchaseDate || !this.assetForm.purchaseCost) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      // Prepare form data
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateAsset");
        data.append("assetId", this.assetForm.id);
      } else {
        data.append("function", "createAsset");
      }

      // Add all form fields
      data.append("name", this.assetForm.name);
      data.append("assetType", this.assetForm.assetType);
      data.append("tagNumber", this.assetForm.tagNumber);
      data.append("serialNumber", this.assetForm.serialNumber);
      data.append("purchaseDate", this.assetForm.purchaseDate);
      data.append("purchaseCost", this.assetForm.purchaseCost);
      data.append("departmentId", this.assetForm.departmentId);
      data.append("status", this.assetForm.status);
      data.append("warrantyExpiry", this.assetForm.warrantyExpiry);
      data.append("supplier", this.assetForm.supplier);
      data.append("notes", this.assetForm.notes);

      // Send the request
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", this.isEditMode ? "Asset updated successfully" : "Asset created successfully");

              // Close the modal
              this.showAssetModal = false;

              // Refresh the assets data
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error saving asset:", res.data.message);
              this.showToast("error", `Failed to ${this.isEditMode ? 'update' : 'create'} asset: ${res.data.message}`);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", `Network error when ${this.isEditMode ? 'updating' : 'creating'} asset`);
          });
    },

    viewAssetDetails(asset) {
      this.selectedAsset = null;
      this.getAssetDetails(asset.id);
    },

    getAssetDetails(assetId) {
      const data = new FormData();
      data.append("function", "getAssetDetails");
      data.append("assetId", assetId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedAsset = res.data.data.selectedAsset;
              this.showDetailsModal = true;
            } else {
              console.error("Error fetching asset details:", res.data.message);
              this.showToast("error", "Failed to load asset details: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching asset details");
          });
    },

    // Asset Assignment Methods
    assignAsset(asset) {
      this.selectedAsset = asset;
      this.assignmentForm = {
        employeeId: "",
        assignmentDate: new Date().toISOString().split('T')[0],
        notes: "",
        notifyEmployee: true
      };
      this.showDetailsModal = false;
      this.showAssignModal = true;
    },

    confirmAssignAsset() {
      if (!this.assignmentForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return;
      }

      const data = new FormData();
      data.append("function", "assignAsset");
      data.append("assetId", this.selectedAsset.id);
      data.append("employeeId", this.assignmentForm.employeeId);
      data.append("assignmentDate", this.assignmentForm.assignmentDate);
      data.append("notes", this.assignmentForm.notes);
      data.append("notifyEmployee", this.assignmentForm.notifyEmployee ? 1 : 0);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Asset assigned successfully");
              this.showAssignModal = false;
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error assigning asset:", res.data.message);
              this.showToast("error", "Failed to assign asset: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when assigning asset");
          });
    },

    unassignAsset(asset) {
      this.selectedAsset = asset;
      this.unassignmentForm = {
        returnDate: new Date().toISOString().split('T')[0],
        returnCondition: "Good",
        notes: ""
      };
      this.showDetailsModal = false;
      this.showUnassignModal = true;
    },

    confirmUnassignAsset() {
      const data = new FormData();
      data.append("function", "unassignAsset");
      data.append("assetId", this.selectedAsset.id);
      data.append("returnDate", this.unassignmentForm.returnDate);
      data.append("returnCondition", this.unassignmentForm.returnCondition);
      data.append("notes", this.unassignmentForm.notes);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Asset unassigned successfully");
              this.showUnassignModal = false;
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error unassigning asset:", res.data.message);
              this.showToast("error", "Failed to unassign asset: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when unassigning asset");
          });
    },

    // Asset Disposal Methods
    disposeAsset(asset) {
      this.selectedAsset = asset;
      this.disposalForm = {
        disposalDate: new Date().toISOString().split('T')[0],
        disposalMethod: "",
        saleAmount: 0,
        reason: "",
        authorizedBy: ""
      };
      this.showDetailsModal = false;
      this.showDisposeModal = true;
    },

    confirmDisposeAsset() {
      if (!this.disposalForm.disposalMethod || !this.disposalForm.reason) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      const data = new FormData();
      data.append("function", "disposeAsset");
      data.append("assetId", this.selectedAsset.id);
      data.append("disposalDate", this.disposalForm.disposalDate);
      data.append("disposalMethod", this.disposalForm.disposalMethod);
      data.append("reason", this.disposalForm.reason);

      if (this.disposalForm.disposalMethod === 'Sold' && this.disposalForm.saleAmount > 0) {
        data.append("saleAmount", this.disposalForm.saleAmount);
      }

      data.append("authorizedBy", this.disposalForm.authorizedBy);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Asset disposed successfully");
              this.showDisposeModal = false;
              this.getAssets();
              this.getRecentActivities();
            } else {
              console.error("Error disposing asset:", res.data.message);
              this.showToast("error", "Failed to dispose asset: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when disposing asset");
          });
    },

    // Asset Maintenance Methods
    addMaintenanceRecord(asset) {
      this.selectedAsset = asset;
      this.maintenanceForm = {
        date: new Date().toISOString().split('T')[0],
        type: "",
        description: "",
        cost: 0,
        performedBy: "",
        newStatus: "Active"
      };
      this.showDetailsModal = false;
      this.showMaintenanceModal = true;
    },

    confirmAddMaintenance() {
      if (!this.maintenanceForm.type || !this.maintenanceForm.description) {
        this.showToast("error", "Please fill in all required fields");
        return;
      }

      const data = new FormData();
      data.append("function", "addAssetMaintenance");
      data.append("assetId", this.selectedAsset.id);
      data.append("date", this.maintenanceForm.date);
      data.append("type", this.maintenanceForm.type);
      data.append("description", this.maintenanceForm.description);
      data.append("cost", this.maintenanceForm.cost);
      data.append("performedBy", this.maintenanceForm.performedBy);
      data.append("newStatus", this.maintenanceForm.newStatus);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", "Maintenance record added successfully");
              this.showMaintenanceModal = false;
              this.getAssets();
              this.getRecentActivities();
              if (this.selectedAsset) {
                this.getAssetDetails(this.selectedAsset.id);
              }
            } else {
              console.error("Error adding maintenance record:", res.data.message);
              this.showToast("error", "Failed to add maintenance record: " + res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when adding maintenance record");
          });
    },

    // Report and Export Methods
    exportAssets() {
      const data = new FormData();
      data.append("function", "exportAssetsReport");

      // Add filter parameters for the export
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key] && key !== 'page' && key !== 'limit') {
          data.append(key, this.filters[key]);
        }
      });

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    exportAssetDetails(asset) {
      const data = new FormData();
      data.append("function", "exportAssetDetails");
      data.append("assetId", asset.id);

      // Use vanilla form submission for file download
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = authUrl;

      // Append each key-value pair to the form
      for (const pair of data.entries()) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = pair[0];
        hiddenField.value = pair[1];
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    // Helper methods for display and formatting
    getAssetIcon(assetType) {
      const iconMap = {
        'Computer': 'mdi mdi-desktop-mac',
        'Laptop': 'mdi mdi-laptop',
        'Mobile Phone': 'mdi mdi-cellphone',
        'Tablet': 'mdi mdi-tablet',
        'Monitor': 'mdi mdi-monitor',
        'Printer': 'mdi mdi-printer',
        'Furniture': 'mdi mdi-chair-rolling',
        'Vehicle': 'mdi mdi-car',
        'Office Equipment': 'mdi mdi-tools',
        'Software': 'mdi mdi-microsoft',
        'Other': 'mdi mdi-package-variant-closed'
      };

      return iconMap[assetType] || 'mdi mdi-package-variant-closed';
    },

    getAssetTypeColor(type) {
      const colorMap = {
        'Computer': 'bg-primary',
        'Laptop': 'bg-info',
        'Mobile Phone': 'bg-success',
        'Tablet': 'bg-purple',
        'Monitor': 'bg-indigo',
        'Printer': 'bg-teal',
        'Furniture': 'bg-orange',
        'Vehicle': 'bg-warning',
        'Office Equipment': 'bg-secondary',
        'Software': 'bg-pink',
        'Other': 'bg-dark'
      };

      return colorMap[type] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      const statusMap = {
        'Active': 'hr-badge-success',
        'Assigned': 'hr-badge-primary',
        'Under Maintenance': 'hr-badge-warning',
        'Disposed': 'hr-badge-danger',
        'Out of Order': 'hr-badge-danger',
        'In Storage': 'hr-badge-info'
      };

      return statusMap[status] || 'hr-badge-secondary';
    },

    getActivityIconClass(type) {
      const typeMap = {
        'create': 'dot-primary',
        'update': 'dot-info',
        'assign': 'dot-success',
        'unassign': 'dot-warning',
        'maintenance': 'dot-purple',
        'dispose': 'dot-danger'
      };

      return typeMap[type] || 'dot-muted';
    },

    getAssetTypeBarClass(type) {
      const colorMap = {
        'Computer': 'bar-primary',
        'Laptop': 'bar-info',
        'Mobile Phone': 'bar-purple',
        'Tablet': 'bar-info',
        'Monitor': 'bar-primary',
        'Printer': 'bar-info',
        'Furniture': 'bar-warning',
        'Vehicle': 'bar-warning',
        'Office Equipment': 'bar-muted',
        'Software': 'bar-purple',
        'Other': 'bar-muted'
      };

      return colorMap[type] || 'bar-muted';
    },

    getActivityIcon(type) {
      const iconMap = {
        'create': 'mdi mdi-plus',
        'update': 'mdi mdi-pencil',
        'assign': 'mdi mdi-account-check',
        'unassign': 'mdi mdi-account-remove',
        'maintenance': 'mdi mdi-tools',
        'dispose': 'mdi mdi-delete'
      };

      return iconMap[type] || 'mdi mdi-information';
    },

    getTimelineMarkerClass(type) {
      const typeMap = {
        'create': 'marker-primary',
        'update': 'marker-info',
        'assign': 'marker-success',
        'unassign': 'marker-warning',
        'maintenance': 'marker-info',
        'dispose': 'marker-danger'
      };

      return typeMap[type] || 'marker-muted';
    },

    getTimelineIcon(type) {
      const iconMap = {
        'create': 'mdi mdi-plus',
        'update': 'mdi mdi-pencil',
        'assign': 'mdi mdi-account-check',
        'unassign': 'mdi mdi-account-remove',
        'maintenance': 'mdi mdi-tools',
        'dispose': 'mdi mdi-delete'
      };

      return iconMap[type] || 'mdi mdi-information';
    },

    getAssetTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalAssets) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      return ((count / this.metrics.totalAssets) * 100).toFixed(1);
    },

    getInitials(name) {
      if (!name) return '';

      return name
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .join('')
          .substr(0, 2);
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';

      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      return new Date(dateTimeString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    },

    formatCurrency(value,currency) {
      if (!value) return `${currency}0.00`;

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(value);
    },

    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        // Fallback to alert if toast plugin not available
        alert(message);
      }
    }
  },
  watch: {
    'filters.assetType': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 300),

    'filters.departmentId': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 300),

    'filters.status': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 300),

    'filters.search': _.debounce(function() {
      this.filters.page = 1; // Reset to first page
      this.getAssets();
    }, 500)
  }
};
</script>